// Margin Calculator — bookmaker margins per market (Phase 3 → M2 wave 9).
//
// Emits the SAME DOM + class names as the legacy renderMarginCalculatorModule,
// so the shared CSS renders it identically (verified by the visual A/B). A pure
// computed table (no controls, no interactivity): the tightest margin per market
// is highlighted green, wide margins (>8%) red. Fixture-only, from the context.
//
// The margins dataset now comes from app-api via `useWidgetData` (was the
// `getMarginCalculatorData` legacyData getter).
//
// Like the other betting widgets, the legacy renderer tags the placed module
// with `betting-module` (dark theme); a layout effect replicates that on the
// enclosing `.placed-module` and removes it on unmount. The ref only exists in
// the LOADED DOM (not the skeleton/error states), so the effect keys on
// `isPending` — it must re-run once the fetch resolves and `.bm-wrapper` mounts,
// otherwise an `[]`-deps effect would fire against the skeleton (ref null) and
// never re-tag on data-arrival. (The wave-9 analogue of the D3 `[chosen, data]`
// dep nuance.) A brief pre-load theme flash is a known, deferred cosmetic — see
// m2-plan.md §10.

import { useLayoutEffect, useRef } from "react";
import { shapeMarginCalculator, marginCellColor } from "@bento/shared";
import { useWidgetData } from "../../data/useWidgetData.js";
import { useFixtureKey } from "../../data/useFixtureKey.js";
import WidgetSkeleton from "./WidgetSkeleton.js";
import WidgetError from "./WidgetError.js";

export interface MarginCalculatorProps {
  fixture: string;
}

export default function MarginCalculator({ fixture }: MarginCalculatorProps) {
  const { data, isPending, isError, refetch } = useWidgetData(
    "margin-calculator",
    useFixtureKey(fixture)
  );

  const hostRef = useRef<HTMLDivElement>(null);
  useLayoutEffect(() => {
    const mod = hostRef.current?.closest<HTMLElement>(".placed-module");
    if (!mod) return;
    mod.classList.add("betting-module");
    return () => mod.classList.remove("betting-module");
    // `isPending` in the deps so the tag lands once `.bm-wrapper` (and thus the
    // ref) mounts on data-arrival — see the header note.
  }, [isPending]);

  if (isPending) return <WidgetSkeleton />;
  if (isError) return <WidgetError onRetry={refetch} />;

  const { markets, rows } = shapeMarginCalculator(fixture, data ?? {});

  return (
    <div className="bm-wrapper" ref={hostRef}>
      <div className="bm-hdr">
        <div className="mc-spacer" />
        {markets.map((m) => (
          <div className="mc-cell bm-hdr-cell" key={m}>
            {m}
          </div>
        ))}
      </div>
      <div className="bm-scroll">
        {rows.map((row, i) => (
          <div
            className={`bm-row${i % 2 === 1 ? " bm-row--alt" : ""}`}
            key={row.bookmaker}
          >
            <div className="mc-badge-col">
              <div className="mc-badge" style={{ background: row.colour }}>
                <span className="mc-badge-txt">{row.abbr}</span>
              </div>
            </div>
            {row.cells.map((c) => (
              <div
                className="mc-cell"
                key={c.market}
                style={{
                  fontWeight: c.isBest ? "700" : "400",
                  color: marginCellColor(c.margin, c.isBest),
                }}
              >
                {c.margin.toFixed(1)}%
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}
